import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RoleBasedSidebar from '../../components/RoleBasedSidebar';
import {
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  getAnnouncementStats
} from '../../services/announcementService';
import '../../styles/DashboardLayout.css';
import '../../styles/announcements/AnnouncementsManagementPage.css';

const AnnouncementsManagementPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'system',
    status: 'draft',
    priority: 'medium',
    pinned: false,
    dismissible: true,
    requireAcknowledgment: false,
    actionButton: { text: '', url: '' },
    targetRoles: [],
    targetCenters: [],
    reappearAfterDays: 0,
    reappearStrategy: 'duration'
  });

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    fetchAnnouncements();
  }, [selectedStatus, selectedType]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {};
      if (selectedStatus !== 'all') filters.status = selectedStatus;
      if (selectedType !== 'all') filters.type = selectedType;

      const response = await getAllAnnouncements(filters);

      if (response.success) {
        setAnnouncements(response.data);
      } else {
        setError('Failed to load announcements');
      }
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      type: 'system',
      status: 'draft',
      priority: 'medium',
      pinned: false,
      dismissible: true,
      requireAcknowledgment: false,
      actionButton: { text: '', url: '' },
      targetRoles: [],
      targetCenters: [],
      reappearAfterDays: 0,
      reappearStrategy: 'duration'
    });
    setShowCreateModal(true);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      type: announcement.type || 'system',
      status: announcement.status || 'draft',
      priority: announcement.priority || 'medium',
      pinned: announcement.pinned || false,
      dismissible: announcement.dismissible !== false,
      requireAcknowledgment: announcement.requireAcknowledgment || false,
      actionButton: announcement.actionButton || { text: '', url: '' },
      targetRoles: announcement.targetRoles || [],
      targetCenters: announcement.targetCenters || [],
      reappearAfterDays: announcement.reappearAfterDays || 0,
      reappearStrategy: announcement.reappearStrategy || 'duration'
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await deleteAnnouncement(id);
      fetchAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      alert(err.message || 'Failed to delete announcement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setFormLoading(true);

      // Clean up actionButton if empty
      const cleanedData = { ...formData };
      if (!cleanedData.actionButton.url || !cleanedData.actionButton.text) {
        cleanedData.actionButton = null;
      }

      // Convert empty targetRoles/targetCenters arrays to indicate "all"
      if (cleanedData.targetRoles.length === 0) {
        cleanedData.targetRoles = [];
      }
      if (cleanedData.targetCenters.length === 0) {
        cleanedData.targetCenters = [];
      }

      if (editingAnnouncement) {
        await updateAnnouncement(editingAnnouncement._id, cleanedData);
      } else {
        await createAnnouncement(cleanedData);
      }

      setShowCreateModal(false);
      fetchAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      alert(err.message || 'Failed to save announcement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleActionButtonChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      actionButton: { ...prev.actionButton, [field]: value }
    }));
  };

  const handleTargetRolesChange = (role) => {
    setFormData(prev => {
      const roles = prev.targetRoles.includes(role)
        ? prev.targetRoles.filter(r => r !== role)
        : [...prev.targetRoles, role];
      return { ...prev, targetRoles: roles };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'draft': return 'badge-secondary';
      case 'archived': return 'badge-muted';
      default: return 'badge-secondary';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'urgent': return 'badge-danger';
      case 'motivation': return 'badge-success';
      case 'feature': return 'badge-primary';
      default: return 'badge-info';
    }
  };

  if (loading && announcements.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-layout">
          <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          <div className="dashboard-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading announcements...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-layout">
        <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />

        <div className="dashboard-content">
          <div className="announcements-page">
            {/* Header */}
            <div className="page-header">
              <div className="header-content">
                <h1>Announcements Management</h1>
                <p className="subtitle">Create and manage system-wide announcements</p>
              </div>
              <div className="header-actions">
                <button onClick={handleCreateNew} className="btn-primary">
                  Create Announcement
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
              <div className="filter-group">
                <label>Status:</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Type:</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Types</option>
                  <option value="system">System</option>
                  <option value="feature">Feature</option>
                  <option value="motivation">Motivation</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <button onClick={fetchAnnouncements} className="btn-secondary">
                Refresh
              </button>
            </div>

            {/* Error State */}
            {error && (
              <div className="error-banner">
                {error}
                <button onClick={fetchAnnouncements} className="btn-text">
                  Try Again
                </button>
              </div>
            )}

            {/* Stats */}
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-value">{announcements.length}</span>
                <span className="stat-label">Total Announcements</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {announcements.filter(a => a.status === 'active').length}
                </span>
                <span className="stat-label">Active</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {announcements.filter(a => a.status === 'draft').length}
                </span>
                <span className="stat-label">Drafts</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {announcements.filter(a => a.pinned).length}
                </span>
                <span className="stat-label">Pinned</span>
              </div>
            </div>

            {/* Announcements List */}
            {announcements.length === 0 ? (
              <div className="empty-state">
                <h3>No announcements yet</h3>
                <p>Create your first announcement to get started</p>
                <button onClick={handleCreateNew} className="btn-primary">
                  Create Announcement
                </button>
              </div>
            ) : (
              <div className="announcements-list">
                {announcements.map(announcement => (
                  <div key={announcement._id} className="announcement-card">
                    <div className="announcement-header">
                      <div className="announcement-title-section">
                        <h3>{announcement.title}</h3>
                        <div className="badges">
                          <span className={`badge ${getStatusBadgeClass(announcement.status)}`}>
                            {announcement.status}
                          </span>
                          <span className={`badge ${getTypeBadgeClass(announcement.type)}`}>
                            {announcement.type}
                          </span>
                          {announcement.pinned && (
                            <span className="badge badge-warning">📌 Pinned</span>
                          )}
                          {announcement.requireAcknowledgment && (
                            <span className="badge badge-info">Requires Ack</span>
                          )}
                        </div>
                      </div>
                      <div className="announcement-actions">
                        <button
                          onClick={() => handleEdit(announcement)}
                          className="btn-icon"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(announcement._id)}
                          className="btn-icon btn-danger"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    <p className="announcement-content">{announcement.content}</p>

                    <div className="announcement-meta">
                      <div className="meta-item">
                        <strong>Priority:</strong> {announcement.priority}
                      </div>
                      <div className="meta-item">
                        <strong>Target Roles:</strong>{' '}
                        {announcement.targetRoles.length === 0
                          ? 'All roles'
                          : announcement.targetRoles.join(', ')}
                      </div>
                      <div className="meta-item">
                        <strong>Created:</strong> {formatDate(announcement.createdAt)}
                      </div>
                      {announcement.updatedAt && (
                        <div className="meta-item">
                          <strong>Updated:</strong> {formatDate(announcement.updatedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="announcement-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Enter announcement title"
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  required
                  rows={4}
                  maxLength={1000}
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <option value="system">System</option>
                    <option value="feature">Feature</option>
                    <option value="motivation">Motivation</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Target Roles (leave empty for all)</label>
                <div className="checkbox-group">
                  {['admin', 'teacher', 'parent', 'student'].map(role => (
                    <label key={role} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.targetRoles.includes(role)}
                        onChange={() => handleTargetRolesChange(role)}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.pinned}
                    onChange={(e) => handleInputChange('pinned', e.target.checked)}
                  />
                  Pin this announcement (appears first)
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.dismissible}
                    onChange={(e) => handleInputChange('dismissible', e.target.checked)}
                  />
                  Allow users to dismiss
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.requireAcknowledgment}
                    onChange={(e) => handleInputChange('requireAcknowledgment', e.target.checked)}
                  />
                  Require acknowledgment
                </label>
              </div>

              <div className="form-group">
                <label>Action Button (optional)</label>
                <input
                  type="text"
                  value={formData.actionButton.text}
                  onChange={(e) => handleActionButtonChange('text', e.target.value)}
                  placeholder="Button text (e.g., Learn More)"
                />
                <input
                  type="url"
                  value={formData.actionButton.url}
                  onChange={(e) => handleActionButtonChange('url', e.target.value)}
                  placeholder="Button URL (e.g., https://example.com)"
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Saving...' : (editingAnnouncement ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsManagementPage;
